/* eslint-disable @typescript-eslint/no-non-null-assertion */
import dagre from "@dagrejs/dagre";
import {
  BracketSeries,
  Bracket as BracketType,
  GameId,
  useGetTournamentBrackets,
} from "@portal-frontend-ssr/blast-api";
import tailwindConfig from "@portal-frontend-ssr/ui/src/tailwind/tailwind.config";
import classNames from "classnames";
import { DateTime } from "luxon";
import { memo, useMemo, useState } from "react";
import ReactFlow, { CoordinateExtent, Handle, Position, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { useIntersectionObserver } from "usehooks-ts";
import { TeamSlot } from "~/routes/_cs.cs.bounty-simulator._index/components/TeamDropdown";
import { FlowBracketMatch } from "./components/FlowBracketMatch";

const nodeWidth = 200;
const nodeHeight = 98;

const TinyNode = () => {
  return (
    <div
      className="invisible"
      style={{
        width: nodeWidth,
        height: nodeHeight,
      }}
    >
      <Handle type="source" position={Position.Right} />
      <Handle type="target" position={Position.Left} />
    </div>
  );
};

const dummyNode = (id: string) => {
  return {
    id,
    style: { opacity: 1 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    hidden: false,
    type: "tinyNode",
    data: {
      match: {
        type: "",
        uuid: "",
        name: "dummynode",
        timeOfSeries: DateTime.now().toISO(),
        index: 0,
        teamA: null,
        teamB: null,
        teamAScore: 0,
        teamBScore: 0,
        isLive: false,
        isCompleted: false,
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setSelectedTeam: () => {},
      selectedTeam: undefined,
      tournamentName: "",
      circuitName: "",
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onTeamClick: () => {},
      pickedResults: {},
    },
    hasTarget: false,
    winnerGoesTo: undefined,
    loserGoesTo: undefined,
    width: nodeWidth,
    height: nodeHeight,
  };
};

const MatchNode = ({
  data,
}: {
  data: {
    match: BracketSeries;
    selectedTeam: string | undefined;
    setSelectedTeam: (team: string) => void;
    tournamentName: string;
    gameId: GameId;
    onTeamClick?: (match: BracketSeries, team: string | null) => void;
    pickedResults?: Record<string, TeamSlot>;
  };
}) => {
  const { match, selectedTeam, setSelectedTeam, tournamentName, gameId, onTeamClick, pickedResults } = data;

  return (
    <FlowBracketMatch
      match={match}
      tournamentName={tournamentName}
      gameId={gameId}
      setSelectedTeam={setSelectedTeam}
      selectedTeam={selectedTeam}
      width={nodeWidth}
      height={nodeHeight}
      onTeamClick={onTeamClick}
      pickedResults={pickedResults}
    />
  );
};

const MatchNodeMemo = memo(MatchNode);

const nodeTypes = {
  match: MatchNodeMemo,
  tinyNode: TinyNode,
};

type FlowBracketProps = {
  tournamentId: string;
  stageId: string;
  gameId?: "cs" | "dota" | "gaming" | "deadlock" | "rl";
  bracket?: BracketType;
  onTeamClick?: (match: BracketSeries, team: TeamSlot | null) => void;
  pickedResults?: Record<string, TeamSlot>;
};

const Bracket = ({ tournamentId, stageId, gameId = "cs", bracket, onTeamClick, pickedResults }: FlowBracketProps) => {
  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(undefined);
  const { data: bracketsData } = useGetTournamentBrackets({
    tournamentId,
    gameId,
    enabled: !!tournamentId && !bracket,
  });

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  const playoffs = bracket || bracketsData?.find((bracket) => bracket.tournamentUuid === stageId);

  const nodes = useMemo(() => {
    if (!playoffs || !playoffs.matches) return [];

    return playoffs.matches.map((match) => ({
      id: match.uuid,
      type: "match",
      data: {
        match,
        setSelectedTeam,
        selectedTeam,
        tournamentName:
          playoffs.parentTournamentName !== "" && playoffs.parentTournamentName !== "no-parent"
            ? playoffs.parentTournamentName
            : playoffs.tournamentName,
        circuitName: playoffs.circuitName,
        onTeamClick,
        pickedResults,
      },
      hasTarget: !!match.winnerGoesTo?.seriesUUID,
      winnerGoesTo: match.winnerGoesTo?.seriesUUID,
      loserGoesTo: match.loserGoesTo?.seriesUUID,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      width: nodeWidth,
      style: {},
      height: nodeHeight,
      hidden: false,
    }));
  }, [onTeamClick, pickedResults, playoffs, selectedTeam]);

  const edges = useMemo(() => {
    if (!playoffs || !playoffs.matches) return [];

    return playoffs.matches
      .filter((match) => match.winnerGoesTo?.seriesUUID)
      .map((match) => {
        const hasScore = match.isLive || match.isCompleted || match.teamAScore > 0 || match.teamBScore > 0;
        const winnerTeam = match.teamAScore > match.teamBScore ? match.teamA?.uuid : match.teamB?.uuid;

        return {
          type: "smoothstep",
          id: `stepedge-${match.uuid}-${match.winnerGoesTo!.seriesUUID}`,
          source: match.uuid,
          target: match.winnerGoesTo!.seriesUUID,
          zIndex: [match.teamA?.uuid, match.teamB?.uuid].includes(selectedTeam) ? 2 : 0,
          style: {
            stroke:
              hasScore && !!winnerTeam && winnerTeam == selectedTeam
                ? tailwindConfig.theme.colors["primary-100"]
                : tailwindConfig.theme.colors["background-80"],
          },
          animated: hasScore && !!winnerTeam && winnerTeam == selectedTeam,
        };
      });
  }, [playoffs, selectedTeam]);

  // When we've got nodes & edges, we can layout them
  const layouted = useMemo(() => {
    if (!nodes || !edges) return null;

    const rankDir = nodes.some((node) => node.hasTarget) ? "LR" : "TB";

    g.setGraph({
      rankdir: rankDir,
      nodesep: 20,
    });

    const calculateStepsToFinalNode = (nodeId: string) => {
      let currentNode = nodes.find((n) => n.id === nodeId);
      let steps = 0;

      while (currentNode?.winnerGoesTo) {
        steps++;
        currentNode = nodes.find((n) => n.id === currentNode?.winnerGoesTo);
      }

      return steps;
    };

    const stepsToFinal: Record<string, number> = {};

    nodes.forEach((node) => {
      stepsToFinal[node.id] = calculateStepsToFinalNode(node.id);
    });

    // Create ghost nodes that will be used to create the invisible edges

    nodes.forEach((node) => {
      if (node.loserGoesTo) {
        const currentSteps = stepsToFinal[node.id];
        const targetSteps = stepsToFinal[node.loserGoesTo];

        if (targetSteps > currentSteps) {
          // Target should be further from final node
          const diff = targetSteps - currentSteps;

          // Create chain starting from the original node
          let previousInvisibleNodeId = node.id;
          for (let i = 0; i < diff; i++) {
            const invisibleNodeId = `invisible_${node.id}_${i}`;
            nodes.push(dummyNode(invisibleNodeId));
            edges.push({
              type: "straight",
              source: previousInvisibleNodeId,
              target: invisibleNodeId,
              style: { stroke: "transparent" },
              id: `${previousInvisibleNodeId}-${invisibleNodeId}`,
              zIndex: 0,
              animated: true,
            });
            previousInvisibleNodeId = invisibleNodeId;
          }

          // Final link to the original target
          if (node.winnerGoesTo) {
            edges.push({
              source: previousInvisibleNodeId,
              target: node.winnerGoesTo,
              style: { stroke: "transparent" },
              type: "straight",
              id: `${previousInvisibleNodeId}-${node.winnerGoesTo}`,
              zIndex: 0,
              animated: true,
            });
          }
        }
      }
    });

    const invisibleNodesByTarget: Record<string, (typeof nodes)[0]> = {};

    for (let i = nodes.length - 1; i >= 0; i--) {
      // Iterate in reverse
      const node = nodes[i];

      if (node.type == "tinyNode") {
        // find the target node for this node, from edges
        const targetId = edges.find((edge) => edge.source === node.id)?.target;

        if (!targetId) {
          continue;
        }

        const mergedNode = invisibleNodesByTarget[targetId];

        if (mergedNode) {
          // Redirect edges targeting this node to the merged node
          edges.forEach((edge) => {
            if (edge.target === node.id) {
              edge.target = mergedNode.id;
            }
          });

          // Remove this node and its outgoing edge
          nodes.splice(i, 1);
          edges.splice(
            edges.findIndex((edge) => edge.source === node.id),
            1,
          );
        } else {
          invisibleNodesByTarget[targetId] = node;
        }
      }
    }

    // Dagre Layout with all nodes (including invisible)
    nodes.forEach((node) => g.setNode(node.id, node));
    edges.forEach((edge) => g.setEdge(edge.source, edge.target));

    dagre.layout(g);

    // Normalize grandFinal position on large brackets:
    const grandFinalNode = nodes.find((node) => node.winnerGoesTo === undefined);

    if (grandFinalNode) {
      const { x } = g.node(grandFinalNode.id);

      // Find the top most node y position
      const highestNode = nodes.find(
        (node) => g.node(node.id).y === Math.min(...nodes.map((node) => g.node(node.id).y)),
      );
      const lowestNode = nodes.find(
        (node) => g.node(node.id).y === Math.max(...nodes.map((node) => g.node(node.id).y)),
      );

      if (!highestNode || !lowestNode) return;

      const highestY = g.node(highestNode.id).y;
      const lowestY = g.node(lowestNode.id).y;

      // Get the grandFinalNode's y position
      const grandFinalY = (highestY + lowestY) / 2;

      g.setNode(grandFinalNode.id, { ...g.node(grandFinalNode.id), x: x, y: grandFinalY });
    }

    return {
      nodes: nodes.map((node) => {
        const { x, y } = g.node(node.id);

        return { ...node, position: { x, y } };
      }),
      edges,
    };
  }, [nodes, edges, g]);

  const translateExtent = useMemo<CoordinateExtent | null>(() => {
    if (!layouted?.nodes) return null;

    return layouted.nodes.reduce(
      ([[left, top], [right, bottom]], { position }) => [
        [
          Math.min(left, (position ?? { x: Infinity }).x - nodeWidth / 2),
          Math.min(top, (position ?? { y: Infinity }).y - nodeHeight / 2),
        ],
        [
          Math.max(right, (position ?? { x: -Infinity }).x + nodeWidth / 2),
          Math.max(bottom, (position ?? { y: -Infinity }).y + nodeHeight / 2),
        ],
      ],
      [
        [Infinity, Infinity],
        [-Infinity, -Infinity],
      ],
    );
  }, [layouted]);

  const divWidth = useMemo(() => {
    if (!translateExtent) return null;

    return translateExtent[0][0] + translateExtent[1][0];
  }, [translateExtent]);

  const divHeight = useMemo(() => {
    if (!translateExtent) return null;

    return translateExtent[0][1] + translateExtent[1][1];
  }, [translateExtent]);

  const { isIntersecting, ref: endRef } = useIntersectionObserver({
    threshold: 1,
  });

  if (!playoffs || !playoffs.matches) return null;

  return (
    <div className="relative flex w-auto overflow-x-hidden">
      {divHeight && translateExtent && divWidth && (
        <div className="custom-scrollbar relative flex w-auto overflow-x-auto overflow-y-hidden pb-2">
          <div style={{ height: divHeight + 45, minWidth: `${divWidth}px` }} className="flex">
            <ReactFlow
              className=""
              nodes={layouted?.nodes}
              edges={layouted?.edges}
              fitView
              edgesFocusable={false}
              translateExtent={translateExtent}
              preventScrolling={false}
              zoomOnScroll={false}
              nodesDraggable={false}
              elementsSelectable={true}
              nodesConnectable={false}
              zoomOnDoubleClick={false}
              selectNodesOnDrag={false}
              panOnDrag={false}
              nodeTypes={nodeTypes}
              minZoom={1}
              maxZoom={1}
              proOptions={{
                hideAttribution: true,
              }}
            ></ReactFlow>
            <div ref={endRef} className="right-0 top-0" />
          </div>
        </div>
      )}

      <div
        className={classNames(
          "absolute -right-1 top-0 h-full bg-gradient-to-l from-background-100 to-transparent transition-all duration-300",
          {
            "w-0": isIntersecting,
            "w-7": !isIntersecting,
          },
        )}
      />
    </div>
  );
};

export const GenericPlayoffBracket = ({
  tournamentId,
  stageId,
  gameId,
  bracket,
  onTeamClick,
  pickedResults,
}: {
  tournamentId: string;
  stageId: string;
  gameId?: "cs" | "dota" | "gaming" | "deadlock" | "rl";
  bracket?: BracketType;
  onTeamClick?: (match: BracketSeries, team: TeamSlot | null) => void;
  pickedResults?: Record<string, TeamSlot>;
}) => {
  return (
    <ReactFlowProvider>
      <Bracket
        tournamentId={tournamentId}
        stageId={stageId}
        gameId={gameId}
        bracket={bracket}
        onTeamClick={onTeamClick}
        pickedResults={pickedResults}
      />
    </ReactFlowProvider>
  );
};
